import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    if (this.dataset.spGallery) {
      this.addEventListener('click', this.#handleThumbnailClick, { signal, capture: true });
      target?.addEventListener('click', this.#handleOptionClick, { signal, capture: true });
    }
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /**
   * Preview the option image while Shopify verifies the new variant.
   * Prices, inventory and the product form still use VariantUpdateEvent.
   * @param {MouseEvent} event
   */
  #handleOptionClick = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.closest('variant-picker')) return;
    if (input.disabled || input.getAttribute('aria-disabled') === 'true') return;
    if (input.closest('.shopify-section, dialog') !== this.closest('.shopify-section, dialog')) return;

    const slideshow = this.slideshow;
    if (!slideshow) return;

    const mediaId = input.dataset.variantMediaId;
    let index = -1;
    if (mediaId) {
      index = Array.from(slideshow.querySelectorAll('slideshow-slide')).findIndex(
        (slide) => slide.querySelector('.product-media[data-media-id]')?.getAttribute('data-media-id') === mediaId
      );
    }
    // Retain reselecting the current option if it has no image mapping.
    if (index < 0 && input.checked && input.dataset.currentChecked === 'true') {
      index = Number(this.dataset.spVariantIndex || '-1');
    }
    if (Number.isInteger(index) && index >= 0) {
      slideshow.select(index, undefined, { animate: false });
    }
  };

  // Direct thumbnail selection avoids the stock animation temporarily reordering
  // distant slides, which can leave the following swipe at the wrong position.
  #handleThumbnailClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const thumbnail = event.target.closest('.slideshow-controls__thumbnail');
    if (!thumbnail || thumbnail.closest('media-gallery') !== this || !this.slideshow) return;
    const controls = thumbnail.closest('slideshow-controls');
    if (!controls) return;
    const index = Array.from(controls.querySelectorAll('.slideshow-controls__thumbnail')).indexOf(thumbnail);
    if (index < 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.slideshow.select(index, event, { animate: false });
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    if (this.dataset.spGallery && newMediaGallery.dataset.spGallery && this.slideshow) {
      // Variant changes keep the same promotional-first media order. Preserve the
      // existing slideshow so scrolling, gestures and zoom keep their current state.
      const mediaIds = (gallery) =>
        Array.from(gallery.querySelectorAll('slideshow-slide .product-media[data-media-id]'),
          (media) => media.getAttribute('data-media-id')).join(',');
      if (mediaIds(this) === mediaIds(newMediaGallery)) {
        this.dataset.spVariantIndex = newMediaGallery.dataset.spVariantIndex || '0';
        const index = Number(this.dataset.spVariantIndex);
        if (Number.isInteger(index) && index >= 0) {
          this.slideshow.select(index, undefined, { animate: false });
        }
        return;
      }
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
